import { useState, useMemo } from 'react';
import {
  SystemPlanningConfig,
  SystemRecommendations,
  EVType,
} from '../types/systemPlanning';
import {
  calculateRecommendations,
  getHomeBaseLoadKw,
  getTotalEVLoadKw,
} from '../utils/planningCalculations';

const DEFAULT_CONFIG: SystemPlanningConfig = {
  evCount: 1,
  evConfigs: [{ type: 'economy' as EVType }, { type: 'economy' as EVType }, { type: 'economy' as EVType }],
  homeSize: 'medium',
  customHomeLoadKw: 3.5,
  currentSolarKw: 8,
  batteryStorageKwh: 13.5,
};

export interface UseSystemPlanningReturn {
  config: SystemPlanningConfig;
  recommendations: SystemRecommendations;
  updateConfig: (partial: Partial<SystemPlanningConfig>) => void;
  updateEVType: (index: number, type: EVType) => void;
  simulationBaseEVKw: number;
  simulationBaseHomeKw: number;
  isPanelOpen: boolean;
  togglePanel: () => void;
}

export function useSystemPlanning(): UseSystemPlanningReturn {
  const [config, setConfig] = useState<SystemPlanningConfig>(DEFAULT_CONFIG);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const recommendations = useMemo(() => calculateRecommendations(config), [config]);

  const simulationBaseHomeKw = useMemo(() => getHomeBaseLoadKw(config), [config]);
  const simulationBaseEVKw = useMemo(() => getTotalEVLoadKw(config), [config]);

  function updateConfig(partial: Partial<SystemPlanningConfig>) {
    setConfig(prev => ({ ...prev, ...partial }));
  }

  function updateEVType(index: number, type: EVType) {
    setConfig(prev => {
      const newConfigs = [...prev.evConfigs];
      newConfigs[index] = { type };
      return { ...prev, evConfigs: newConfigs };
    });
  }

  function togglePanel() {
    setIsPanelOpen(prev => !prev);
  }

  return {
    config,
    recommendations,
    updateConfig,
    updateEVType,
    simulationBaseEVKw,
    simulationBaseHomeKw,
    isPanelOpen,
    togglePanel,
  };
}
